<!-- Popup: TOC(Table of Contents) -->

<script lang="ts" setup>
import { inject, nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router/composables'
import { useScroll } from '@vueuse/core'
import { api } from '@/api'
import { useLoadingStore } from '@/stores'
import { CHAPTER_LOCK } from '@/constants'

const props = withDefaults(
  defineProps<{
    value: boolean
    book: any
    contentId: string
    chapterId: string
    chapterNo?: number
  }>(),
  {
    chapterNo: 1,
  },
)

defineEmits<{
  (event: 'input', value: boolean): void
}>()

const router = useRouter()
const theme = inject<{ background: string } | null>('theme', { background: 'white' })

const { $showLoading, $hideLoading } = useLoadingStore()

const chapterList = ref<any['data']['records']>([])
const chapterScrollRef = ref()
const { y: chapterScrollY } = useScroll(chapterScrollRef, { behavior: 'instant' })
const chooseChapterOptions = ref(false)
const chapterOptions = ref<any[]>([])
const pagination = ref({
  pageIndex: Math.ceil(props.chapterNo / 100),
  pageSize: 100,
})
async function fetchChapterList() {
  $showLoading()
  try {
    const { data: chapterListData } = await api.get<any, GetKuaishouContentGetcontentchapterbooklistResponse>('/kuaishou/content/getContentChapterBookList', { params: { contentId: props.contentId, ...pagination.value } })
    chapterList.value = chapterListData.records
    const page = chapterListData.pages
    // Note: 章节分页 options
    chapterOptions.value = Array.from({ length: page })
      .map((_, index) => ({
        label: `${(index * pagination.value.pageSize + 1)} - ${(index + 1) * pagination.value.pageSize}章`,
        value: index + 1,
      }))
    // Note: auto scroll to current chapterId
    if (props.chapterId) {
      const itemHeight = 57
      const index = chapterList.value.findIndex((item: any) => item.id === props.chapterId)
      nextTick(() => {
        if (index > -1)
          chapterScrollY.value = index * itemHeight
      })
    }
  }
  finally {
    $hideLoading()
  }
}
async function onOpen() {
  await fetchChapterList()
}
watch(() => pagination.value.pageIndex, async () => {
  await fetchChapterList()
  chooseChapterOptions.value = false
})
</script>

<template>
  <!-- Popup: 查看章节 -->
  <BasicPopup :value="value" @input="value => $emit('input', value)" @open="onOpen">
    <div v-if="book" class="h-90vh overflow-scroll rounded-t-lg" :style="`background: ${theme ? theme.background : ''}`">
      <div class="pt-5 pb-13px px-5 bg-#0000000A">
        <div class="flex space-x-10px">
          <img class="w-55px h-77px rounded-3px" :src="book.contentCoverUrl" alt="图片:书封">
          <div class="flex flex-col justify-center">
            <p class="text-17px font-bold">
              {{ book.contentName }}
            </p>
            <p class="mt-2 text-13px text-#666 line-clamp-2">
              {{ book.introduce }}
            </p>
          </div>
        </div>
        <div class="mt-5 flex items-center space-x-10px text-13px">
          <span class="flex-1 truncate text-#666">
            连载至 {{ book.lastChapter?.chapterName }}
          </span>
          <div
            class="flex items-center space-x-1" :class="{ 'text-primary': chooseChapterOptions }"
            @click="chooseChapterOptions = !chooseChapterOptions"
          >
            <span>{{ chapterOptions[pagination.pageIndex - 1]?.label }}</span>
            <i-carbon-caret-down class="text-#C9CDD4 transition-all duration-500" :class="{ 'rotate-180': chooseChapterOptions, 'text-primary': chooseChapterOptions }" />
          </div>
        </div>
      </div>
      <ul v-show="chooseChapterOptions" class="px-5 h-[calc(90vh-150px)] overflow-scroll">
        <li
          v-for="item in chapterOptions" :key="item.value"
          class="h-57px lh-57px flex items-center justify-between border-b border-#00000010"
          :class="{ 'text-primary': pagination.pageIndex === item.value }"
          @click="pagination.pageIndex = item.value"
        >
          <span>{{ item.label }}</span>
          <i-assets-icon-check v-if="pagination.pageIndex === item.value" class="text-primary w-17px" />
        </li>
      </ul>
      <ul v-show="!chooseChapterOptions" ref="chapterScrollRef" class="px-5 h-[calc(90vh-150px)] overflow-scroll">
        <li
          v-for="item in chapterList" :key="item.id"
        >
          <BizTrackClick
            name="chapterList" type="link" :extra="{ contentId, chapterId: item.id }"
            class="h-57px lh-57px flex items-center justify-between border-b border-#00000010"
            :class="{ 'text-primary': chapterId === item.id }"
            @click="() => router.replace({ name: 'chapter', params: { contentId, chapterId: item.id } })"
          >
            <span>{{ item.chapterName }}</span>
            <i-assets-icon-lock v-if="item.lockStatus === CHAPTER_LOCK.LOCK" class="w-17px" />
          </BizTrackClick>
        </li>
      </ul>
    </div>
  </BasicPopup>
</template>
